import React, { useMemo } from "react";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import ArrowRight from "@mui/icons-material/ArrowRight";
import { TreeView } from "@mui/lab";

import RenderCompany from "./RenderCompany";
import RenderNetwork from "./RenderNetwork";

/*
 * Renders networks recursively from parentNetworkId.
 *
 * Example from the live response:
 * UniQueueNetwork (2034)
 *   └─ UQ Bygg (3)
 *       └─ Söderköpings kommun2 (19)
 *
 * The earlier implementation only rendered one network level.
 */
const BusinessNetworkTree = ({
  handleContextMenu,
  handleDeleteNetwork,
  handleError,
  handleOnNodeToggle,
  handleRenameNetwork,
  handleSetEditNode,
  isEditMode,
  openedNodes,
  parentNetworkId,
  refresh,
  selectedNode,
  treeData,
}) => {
  const networkChildrenByParent = useMemo(() => {
    const result = {};

    Object.values(treeData || {}).forEach(node => {
      if (!node?.isNetwork || node.isRoot) {
        return;
      }

      const parentId = node.parentNetworkId ?? null;

      if (!result[parentId]) {
        result[parentId] = [];
      }

      result[parentId].push(node);
    });

    Object.values(result).forEach(nodes => {
      nodes.sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "sv")
      );
    });

    return result;
  }, [treeData]);

  const renderNetwork = node => {
    const childNetworks = networkChildrenByParent[node.networkId] || [];

    return (
      <RenderNetwork
        key={`network-${node.networkId}`}
        handleContextMenu={handleContextMenu}
        handleDeleteNetwork={handleDeleteNetwork}
        handleError={handleError}
        handleRenameNetwork={handleRenameNetwork}
        handleSetEditNode={handleSetEditNode}
        isEditMode={isEditMode}
        node={node}
        refresh={refresh}
        selectedNode={selectedNode}
        treeData={treeData}
      >
        {childNetworks.map(renderNetwork)}
      </RenderNetwork>
    );
  };

  const topLevelNetworks =
    networkChildrenByParent[parentNetworkId ?? null] || [];

  const directCompanyIds =
    treeData?.[`${parentNetworkId}`]?.childCompanyIds || [];

  if (topLevelNetworks.length === 0 && directCompanyIds.length === 0) {
    return null;
  }

  return (
    <TreeView
      defaultCollapseIcon={<ArrowDropDown />}
      defaultExpandIcon={<ArrowRight />}
      expanded={openedNodes}
      onNodeToggle={handleOnNodeToggle}
    >
      {topLevelNetworks.map(renderNetwork)}

      {directCompanyIds.map(memberId => (
        <RenderCompany
          key={`company-${memberId}`}
          company={treeData[`c_${memberId}`]}
          handleContextMenu={handleContextMenu}
          handleMoveCompany={() => {}}
          treeData={treeData}
        />
      ))}
    </TreeView>
  );
};

export default BusinessNetworkTree;
